import firebase from '../services/firebase';
import { isFunction } from 'lodash';
import { setGuestAvatar, setGuestColor } from './dbUtils';
import { reset as resetAnalytics } from '../analytics/track';

/**
 * Signs out the user
 * @returns {Promise}
 */
export function signOut() {
  resetAnalytics();
  return firebase.auth().signOut();
}

export function signInAnonymously() {
  return firebase.auth().signInAnonymously()
    .then(({ user }) => {
      firebase.database().ref().child(`usersById/${user.uid}`).update({
        uid: user.uid,
      });
      return user;
    });
}

/**
 * Subscribes to any changes to the user's log in state
 * @param {(user: Object) => void} onChange 
 * @returns {function} Unsubscribe method for cleanup
 */
export function subscribeToLoginChanges(onChange) {
  return firebase.auth().onAuthStateChanged(onChange);
}

export function getCurrentUser() {
  return firebase.auth().currentUser;
}

export function addCheckIdToUsersChecks(userId, checkId){
  const date = Date.now();
  return firebase.database().ref(`usersById/${userId}/checkIds`).update({[checkId]: date})
}

export const IMAGE_STRINGS = ['avocado', 'banana', 'lollipop', 'meat', 'mushroom', 'sushi', 'taco'];
export const COLOR_STRINGS = ['green', 'yellow', 'purple', 'blue', 'red', 'gray', 'orange', 'black'];

export function setUserCheckData(userId, myName, checkId, checkMetaData, guests){
  // const date = Date.now();
  const rootRef = firebase.database().ref();
  const guestIds = Object.keys(guests);

  const myUserObj = {
    name: myName,
    didCheckout: false,
    avatar: setGuestAvatar(guestIds.length, IMAGE_STRINGS),
    color: setGuestColor(guestIds.length, COLOR_STRINGS),
  };

  const guestsForCheck = {
    ...guests,
    [userId]: myUserObj,
  };

  const guestsForMeta = guestIds.reduce((guestList, guestId) => ({
    ...guestList,
    [guestId]: { name: guests[guestId].name }
  }),{[userId]: { name: myName }})

  const updatesObj = {
    [`checksByUser/${userId}/${checkId}/checkMetaData`]: checkMetaData,
    [`checksById/${checkId}/guests`]: guestsForCheck,
  };

  [...guestIds, userId ].forEach((guestId) => {
    if (guestId !== 'initial') {
      updatesObj[`checksByUser/${guestId}/${checkId}/guests`] = guestsForMeta;
    }
  });

  return Promise.all([
    rootRef.update(updatesObj),
    firebase.firestore().collection('users').doc(userId).set({ name: myName }, { merge: true }),
  ]);
}

export function setUserCountdownEndDate(userId) {
  const startTime = Date.now();
  const endTime = startTime + (24 * 60 * 60 * 1000);
  return firebase.firestore().collection('users').doc(userId).set({ countdownEndDate: endTime }, { merge: true });
}

/**
 * Subscribes to any updates on the check
 * @param {string} userId 
 * @param {{(checkMetaData: checkMetaData) => void}} onUpdate
 * @returns {function} Unsubscribe method for cleanup
 */
export function subscribeToUserChecks(userId, onUpdate) {
  const ref = firebase.database().ref().child(`checksByUser/${userId}`);

  const updateWrapper = snapshot => {
    isFunction(onUpdate) && onUpdate(snapshot.val());
  };

  ref.on('value', updateWrapper);
  return () => ref.off('value', updateWrapper);
}

export function addCheckToUsersChecksModerated(checkId, userId){
  const date = Date.now();
  return firebase.database().ref(`usersById/${userId}/checksModerated`).update({[checkId]: date})
}

/**
 * Save the payment fields the web host just edited, one path at a time.
 *
 * The app keeps more under the same paymentInfo node than the web dialog can
 * edit (a Zelle phone or email, a cash preference, which way friends see
 * first), so writing the whole node from here would wipe them. Per field paths
 * leave everything else exactly as it was.
 * @param {string} userId
 * @param {Object} paymentObj - only the fields being changed, e.g. { venmo, cashApp }
 * @param {string} [checkId] - also stamp them on this receipt's copy
 */
export function setUserPaymentInfo(userId, paymentObj, checkId){
  const ref = firebase.database().ref();
  const fields = Object.keys(paymentObj ?? {});
  if (!fields.length) return Promise.resolve();

  const updateObj = {};
  fields.forEach((field) => {
    updateObj[`usersById/${userId}/paymentInfo/${field}`] = paymentObj[field];

    if (checkId) {
      updateObj[`checksById/${checkId}/userPaymentInfo/${field}`] = paymentObj[field];
    }
  });

  return ref.update(updateObj)
}

export function setHasSeenInstructions(userId, type, status) {
  return firebase.database().ref(`usersById/${userId}/seenInstructions`).update({ [type]: status });
}

export function subscribeToUser(userId, onUpdate) {
  const ref = firebase.database().ref().child(`usersById/${userId}`);

  const updateWrapper = snapshot => {
    isFunction(onUpdate) && onUpdate(snapshot.val());
  };

  ref.on('value', updateWrapper);
  return () => ref.off('value', updateWrapper);
}

/**
 * Read the saved profile name from Firestore /users/{uid}. Resolves to the
 * trimmed name, or '' when the doc or field is missing. The group join flow
 * uses this to decide whether to ask for a name after phone verification.
 * @param {string} userId
 * @returns {Promise<string>}
 */
export async function getProfileName(userId) {
  const snapshot = await firebase.firestore().collection('users').doc(userId).get();
  const name = snapshot.exists ? snapshot.data().name : '';
  return typeof name === 'string' ? name.trim() : '';
}

/**
 * Save the user's profile name to Firestore /users/{uid} — the SAME field the
 * mobile app writes (splyt-mobile-2 features/User/userApi.ts saveName), so the
 * backend onProfileUpdated trigger repairs 'Member' group snippets and mutuals.
 * Merge so the rest of the profile (pendingGroupJoin etc.) is never clobbered.
 * @param {string} userId
 * @param {string} name - already validated/trimmed by the caller
 */
export function saveProfileName(userId, name) {
  return firebase.firestore().collection('users').doc(userId).set({ name }, { merge: true });
}

/*
  Reports null, not undefined, when the user has no profile document yet.
  Callers need to tell "the read has come back and there is no profile" apart
  from "the read has not come back", and undefined cannot say both. A brand new
  guest has no document until they save a name, so this distinction is what
  lets the receipt know to ask for one.
*/
export function subscribeToProfile(userId, func) {
  return firebase.firestore().collection('users').doc(userId).onSnapshot(snapshot => {
    if (snapshot.exists) {
      func(snapshot.data());
    } else if (snapshot.metadata.fromCache) {
      // The SDK reports a never fetched document as missing from cache once
      // it gives up reaching the backend. That is "unknown", not "absent":
      // treating it as absent would ask a returning guest for a name again
      // and overwrite the one they have. Stay at undefined (still loading).
    } else {
      func(null);
    }
  });
}

export function sendSplytSMS(recipient, link) {
  return firebase.functions().httpsCallable('smsSendSplytLink')({ recipient, link });
}

export function sendAppStoreLinkSMS(recipient) {
  return firebase.functions().httpsCallable('smsSendAppStoreLink')({ recipient });
}
